import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { useMediaQuery } from "../browser.js";
import { useModel, useTitle } from "../context.js";
import { liftsKey, loadLifts, useResource } from "../data.js";
import { addDays, dayShort, mondayOf } from "../model/days.js";
import { loadStatus, trendShort, trendText, type LoadData } from "../model/load.js";
import { plural } from "../model/plan.js";
import type { Athlete } from "../model/week.js";
import { href } from "../paths.js";
import { byNumber, byText, type Column, PageHeader, PbMark, Status, Table } from "../../kit/index.js";
import { AthletePage, columnText, LoadExplainer, weekText } from "./Athlete.js";
import { NamedWithNotes, useNotes } from "./Notes.js";
import { athleteLoad, LoadCell, loadValue, useLoad, type AthleteLoad } from "./loadParts.js";
import { daysAway, inDays, nextMeetFor } from "../model/meets.js";
import type { Meet } from "../model/types.js";
import { useMeets } from "./Meets.js";

export function TrendTag({ data, id, today, week }: { data: LoadData | undefined; id: string; today: string; week?: string }) {
  const athlete = data?.athletes.find((a) => a.id === id);
  if (!data || !athlete) return null;
  let index = week ? data.weeks.indexOf(week) : data.weeks.length - 1;
  if (index < 0) return null;
  // The week still running is partial, so a list reads the last full week, as the athlete page leads with.
  const shifted = data.weeks[index] === mondayOf(today) && index > 0;
  if (shifted) index -= 1;
  const status = loadStatus(data.weeks, athlete, index);
  const short = trendShort(status);
  if (!short) return null;
  return (
    <span className={"trend-tag is-" + status.trend} title={(shifted ? "Last week: " : "") + trendText(status)}>
      <span aria-hidden="true">{status.trend === "high" ? "↑" : "↓"}</span>
      <span>{short}</span>
      {shifted ? <span className="sr"> last week</span> : null}
    </span>
  );
}

interface Row {
  athlete: Athlete;
  load: AthleteLoad | null;
  /** Personal bests in the last twelve weeks; null while the team's lifts load. */
  bests: number | null;
  meet: Meet | undefined;
}

/** Personal bests set in weighed lifts since twelve Mondays ago, per athlete. */
function useRecentBests(today: string): Map<string, number> | null {
  const lifts = useResource(liftsKey, loadLifts).value;
  return useMemo(() => {
    if (!lifts) return null;
    const from = addDays(mondayOf(today), -7 * 11);
    const counts = new Map<string, number>();
    for (const lift of lifts.list)
      for (const [id, sets] of lift.athletes) for (const x of sets) if (x.record && x.date >= from) counts.set(id, (counts.get(id) ?? 0) + 1);
    return counts;
  }, [lifts, today]);
}

function ratio(a: Athlete): number {
  return a.total ? a.done / a.total : -1;
}

/** Everyone on the team as a table (DESIGN §16.6); a row opens the athlete in the panel beside it. */
export function Athletes() {
  const model = useModel();
  const { id } = useParams();
  const load = useLoad();
  const bests = useRecentBests(model.today);
  const notes = useNotes();
  const meets = useMeets(model.today, addDays(model.today, 365));
  const open = id ? model.athletes.find((a) => a.id === id) : undefined;
  useTitle(id ? (open?.name ?? "Athlete") : "Athletes");

  const rows: Row[] = model.athletes.map((a) => ({ athlete: a, load: athleteLoad(load, a.id, model.today), bests: bests ? (bests.get(a.id) ?? 0) : null, meet: nextMeetFor(meets, a.id, model.today) }));
  const columns: Column<Row>[] = [
    { key: "name", label: "Name", sticky: true, sort: byText((r) => r.athlete.name), render: (r) => <NamedWithNotes id={r.athlete.id} name={r.athlete.name} notes={notes} /> },
    {
      key: "column",
      label: "Column this week",
      className: "k-hide-phone",
      sort: byText((r) => columnText(r.athlete)),
      render: (r) => {
        const text = columnText(r.athlete);
        return r.athlete.column ? text : <span className="k-muted">{text || "—"}</span>;
      },
    },
    {
      key: "week",
      label: "This week",
      sort: (a, b) => ratio(a.athlete) - ratio(b.athlete) || b.athlete.missed - a.athlete.missed,
      render: ({ athlete: a }) => (
        <span className="ath-week">
          <span className={"num" + (a.total ? "" : " k-muted")}>{weekText(a)}</span>
          {a.missed ? <Status kind="missed" text={`${a.missed} not logged`} /> : null}
          <span className="k-sub k-phone-only">{[columnText(a), a.lastWorkout ? "last " + dayShort(a.lastWorkout) : null].filter(Boolean).join(" · ")}</span>
        </span>
      ),
    },
    { key: "load", label: "Load", sort: byNumber((r) => loadValue(r.load)), render: (r) => <LoadCell load={r.load} /> },
    {
      key: "last",
      label: "Last trained",
      className: "k-hide-phone",
      sort: byText((r) => r.athlete.lastWorkout ?? ""),
      render: (r) => (r.athlete.lastWorkout ? <span className="num">{dayShort(r.athlete.lastWorkout)}</span> : <span className="k-muted">Not yet</span>),
    },
    {
      key: "bests",
      label: "Personal bests, 12 weeks",
      className: "k-hide-phone",
      align: "end",
      sort: byNumber((r) => r.bests ?? -1),
      render: (r) =>
        r.bests == null ? <span className="k-muted"><span aria-hidden="true">…</span><span className="sr">Loading</span></span> : r.bests ? <PbMark n={r.bests} /> : <span className="k-muted"><span aria-hidden="true">—</span><span className="sr">None</span></span>,
    },
    {
      key: "meet",
      label: "Next meet",
      wrap: true,
      sort: byNumber((r) => (r.meet ? daysAway(r.meet, model.today) : Number.MAX_SAFE_INTEGER)),
      render: (r) => (r.meet ? <span>{r.meet.name + " · " + inDays(daysAway(r.meet, model.today))}</span> : null),
    },
  ];

  const phone = useMediaQuery("(max-width: 860px)");

  if (id) return <AthletePage id={id} backTo="/athletes" />;
  const context = plural(rows.length, "athlete", "athletes") + " · this week's sessions and load";
  return (
    <div className="k-page">
      <PageHeader title="Athletes" context={context} />
      {rows.length ? (
        <>
          {phone ? <AthleteList rows={rows} today={model.today} monday={model.monday} notes={notes} /> : (
          <Table
            rows={rows}
            columns={columns}
            rowKey={(r) => r.athlete.id}
            href={(r) => href(model.monday, "athlete", r.athlete.id)}
            selected={id ?? null}
            label="Athletes"
            initialSort={{ key: "name", dir: "asc" }}
          />
          )}
          <LoadExplainer />
        </>
      ) : (
        <p className="k-empty" role="status">
          No athletes yet. They appear here once they join with their code.
        </p>
      )}
    </div>
  );
}

/** On a phone (§33): one row an athlete, the week and the next meet under the name, load beside it. */
function AthleteList({ rows, today, monday, notes }: { rows: Row[]; today: string; monday: string; notes: ReturnType<typeof useNotes> }) {
  const sorted = [...rows].sort((a, b) => a.athlete.name.localeCompare(b.athlete.name));
  return (
    <ul className="ath-list" aria-label="Athletes">
      {sorted.map((r) => {
        const a = r.athlete;
        const line = [weekText(a), columnText(a), a.lastWorkout ? "last " + dayShort(a.lastWorkout) : null, r.meet ? r.meet.name + " " + inDays(daysAway(r.meet, today)) : null].filter(Boolean).join(" · ");
        return (
          <li key={a.id}>
            <Link className="ath-list-row" to={href(monday, "athlete", a.id)}>
              <span className="ath-list-main">
                <NamedWithNotes id={a.id} name={a.name} notes={notes} />
                <span className="ath-list-sub">{line}</span>
                {a.missed ? <Status kind="missed" text={`${a.missed} not logged`} /> : null}
              </span>
              <span className="ath-list-load">
                <LoadCell load={r.load} />
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
